<template>
  <div>
    <NavBar />
    <div class="page scanner-page">

      <div class="scanner-hero">
        <div class="hero-copy">
          <span class="hero-kicker">Encaissement terrain</span>
          <h1>Ventes</h1>
          <p>Scannez une carte, retrouvez un client ou saisissez une vente sans fidélité en quelques secondes.</p>
        </div>
        <div class="hero-actions">
          <div class="status-pill" :class="{ online: isOnline, offline: !isOnline }">
            <span class="status-dot"></span>
            {{ isOnline ? 'En ligne' : 'Hors ligne' }}
          </div>
          <div class="market-pill" v-if="villeActive">
            <span class="pill-icon location-icon"></span>
            {{ marcheActifLabel }}
          </div>
          <div class="market-pill warn" v-else>
            <span class="pill-icon alert-icon"></span>
            Aucun marché sélectionné
          </div>
          <button class="btn btn-secondary nouveau-btn" @click="$router.push('/nouveau-client')">
            <span class="btn-plus"></span>
            Nouveau client
          </button>
        </div>
      </div>

      <div class="active-day-panel" :class="{ blocked: encaissementBloque }">
        <div class="active-day-grid">
          <div>
            <span class="section-kicker">Marché actif</span>
            <strong>{{ marcheActifLabel || 'Non sélectionné' }}</strong>
          </div>
          <div>
            <span class="section-kicker">Journée active</span>
            <strong>{{ journeeActiveLabel }}</strong>
          </div>
          <button class="btn btn-secondary active-day-action" type="button" @click="$router.push('/dashboard')">
            Choisir / ouvrir le marché du jour
          </button>
        </div>
        <p v-if="encaissementBloque" class="active-day-alert">
          {{ encaissementErreur || 'Encaissement bloqué : ouvrez la journée du marché avant de vendre.' }}
        </p>
        <p v-if="alerteVilleLocale" class="active-day-alert">
          {{ alerteVilleLocale }}
        </p>
        <div v-if="encaissementBloque && marchesOuverts.length" class="market-choice-list">
          <span class="section-kicker">Journées ouvertes aujourd'hui</span>
          <div class="market-choice-grid">
            <button
              v-for="marche in marchesOuverts"
              :key="marche.marche_lieu_id || marche.journee_id || marche.id || marche.ville"
              class="market-choice-btn"
              type="button"
              :disabled="selectionMarcheLoading"
              @click="selectionnerMarcheScanner(marche)"
            >
              <strong>{{ libelleMarche(marche) }}</strong>
              <span>{{ marche.statut === 'cloture' ? 'Clôturée' : 'Ouverte' }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="scanner-grid">
        <section class="scanner-main">
          <!-- Recherche + caméra -->
          <div class="card scanner-card search-card">
            <div class="section-heading">
              <div>
                <span class="section-kicker">Vente avec carte</span>
                <h2>Rechercher ou scanner</h2>
              </div>
              <button class="btn-camera" @click="toggleCamera" :class="{ active: cameraActive }" title="Scanner avec la caméra">
                <span class="camera-icon"></span>
              </button>
            </div>

            <div class="search-row">
              <input
                v-model="recherche"
                class="input search-input"
                placeholder="Prénom, nom, téléphone ou numéro de carte..."
                @input="rechercherClient"
              />
            </div>
            <p class="hint">Tapez au moins 2 caractères ou scannez la carte avec la caméra.</p>

            <div class="camera-container" v-if="cameraActive">
              <div class="camera-topline">
                <span>Caméra active</span>
                <button class="camera-close" @click="fermerCamera">Fermer</button>
              </div>
              <div id="qr-reader"></div>
            </div>

            <div class="scan-success" v-if="scanMessage">
              <span class="success-icon"></span>
              {{ scanMessage }}
            </div>

            <div class="activation-card" v-if="carteAActiver">
              <div class="activation-header">
                <div>
                  <span class="section-kicker">Carte disponible</span>
                  <h3>Carte à activer</h3>
                </div>
                <span class="activation-badge">Nouvelle carte</span>
              </div>
              <p class="activation-number">N° {{ carteAActiver.numero_carte }}</p>
              <p class="hint activation-hint">Cette carte physique n’est pas encore rattachée à un client.</p>
              <div class="activation-actions">
                <button class="btn btn-primary" @click="creerClientAvecCarte">Créer un client avec cette carte</button>
                <button class="btn btn-secondary" @click="rattacherClientExistant">Rattacher à un client existant</button>
              </div>
            </div>

            <div class="loading-box" v-if="loading">
              <span class="loader-dot"></span>
              Recherche en cours...
            </div>

            <div class="resultats" v-if="resultats.length > 0">
              <div class="resultat-item" v-for="c in resultats" :key="c.id" @click="selectionnerResultat(c)">
                <div class="resultat-avatar">{{ c.prenom[0] }}{{ c.nom ? c.nom[0] : '' }}</div>
                <div class="resultat-info">
                  <strong>{{ c.prenom }} {{ c.nom }}</strong>
                  <span>{{ c.telephone }} · Carte {{ c.numero_carte }}</span>
                </div>
                <div class="resultat-points">
                  <strong>{{ c.points_solde }}</strong>
                  <span>pts</span>
                </div>
              </div>
            </div>

            <div v-if="recherche.length >= 2 && resultats.length === 0 && !loading" class="aucun-resultat-box">
              <div class="empty-icon"></div>
              <p>Aucun client trouvé pour <strong>{{ recherche }}</strong></p>
              <button class="btn btn-primary empty-action" @click="inscrireDepuisRecherche">
                <span class="btn-plus"></span>
                Inscrire ce client
              </button>
            </div>
          </div>

          <!-- Fiche client -->
          <div v-if="client && !resultat" class="card scanner-card client-card">
            <div class="client-header">
              <div class="client-identity">
                <div class="client-avatar">{{ client.prenom[0] }}{{ client.nom ? client.nom[0] : '' }}</div>
                <div class="client-info">
                  <span class="section-kicker">Client sélectionné</span>
                  <h2>{{ client.prenom }} {{ client.nom }}</h2>
                  <p class="client-tel">{{ client.telephone }}</p>
                  <p class="client-carte">Carte n° {{ client.numero_carte }}</p>
                </div>
              </div>
              <div class="client-points">
                <span class="points-value">{{ client.points_solde }}</span>
                <span class="points-label">points</span>
                <span class="points-seuil" v-if="client.points_solde < seuilPoints">{{ seuilPoints - client.points_solde }} pts avant réduction</span>
                <span class="points-dispo" v-else>Réduction disponible</span>
              </div>
            </div>

            <!-- Alerte bon d'achat -->
            <div class="bon-alerte" v-if="bonBientotExpire" :class="bonBientotExpire.jours_restants <= 7 ? 'bon-urgent' : 'bon-warning'">
              <span class="bon-alerte-icon">🎟️</span>
              <div>
                <strong>Bon d'achat {{ formatEuro(bonBientotExpire.valeur_euros) }}</strong>
                <span v-if="bonBientotExpire.jours_restants <= 0"> — ⚠️ Expiré !</span>
                <span v-else-if="bonBientotExpire.jours_restants <= 7"> — ⚠️ Expire dans {{ bonBientotExpire.jours_restants }} jour(s) !</span>
                <span v-else> — Expire le {{ formatDate(bonBientotExpire.date_expiration) }}</span>
              </div>
            </div>
            <div class="sale-panel">
              <div class="montant-section">
                <label class="label">Montant de l'achat (€)</label>
                <input v-model.number="montant" class="input montant-input" type="number" min="0" step="0.01" placeholder="0.00" ref="montantInput"
                  @focus="montantFocus = true" @blur="montantFocus = false" @keydown.enter.prevent="validerSiPossible" />
                <div class="points-preview" v-if="montant > 0">
                  <span>Points à gagner</span>
                  <strong>+{{ pointsPreview }} pts</strong>
                </div>
              </div>

              <!-- Mode de paiement -->
              <div class="paiement-section">
                <label class="label">Mode de paiement</label>
                <div class="paiement-toggle">
                  <button class="paiement-btn" :class="{ active: modePaiement === 'especes' }" @click="modePaiement = 'especes'">
                    <span class="cash-icon"></span>
                    Espèces
                  </button>
                  <button class="paiement-btn" :class="{ active: modePaiement === 'cb' }" @click="modePaiement = 'cb'">
                    <span class="card-icon"></span>
                    CB
                  </button>
                </div>
              </div>
            </div>

            <p v-if="erreur" class="error">{{ erreur }}</p>

            <div class="actions">
              <button ref="btnValider" class="btn btn-primary btn-valider" :class="{ 'btn-valider--clavier': montantFocus }" :style="{ '--clavier-offset': clavierOffset + 'px' }"
                @mousedown.prevent @click="validerTransaction" :disabled="!venteValidable">
                {{ loadingTx ? 'Enregistrement...' : 'Valider la vente' }}
              </button>
              <button class="btn btn-secondary" @click="reset">Annuler</button>
            </div>
          </div>

          <!-- Résultat transaction -->
          <div v-if="resultat" class="card scanner-card resultat-card">
            <div class="resultat-icon">{{ resultat.bon_genere ? '🎉' : '✓' }}</div>
            <div class="bon-genere-card" v-if="resultat.bon_genere">
              <div class="bon-genere-titre">Bon d'achat généré</div>
              <div class="bon-genere-valeur">{{ formatEuro(resultat.bon_genere.valeur_euros) }}</div>
              <div class="bon-genere-exp">Valable jusqu'au {{ formatDate(resultat.bon_genere.date_expiration) }}</div>
            </div>
            <h2>Transaction enregistrée</h2>
            <p class="resultat-msg">{{ resultat.message }}</p>
            <div class="resultat-detail">
              <div class="stat">
                <span>Nouveau solde</span>
                <strong>{{ resultat.nouveau_solde }} pts</strong>
              </div>
              <div class="stat" v-if="resultat.reduction > 0">
                <span>Réduction appliquée</span>
                <strong class="reduction-applied">-{{ formatEuro(resultat.reduction) }}</strong>
              </div>
            </div>
            <button class="btn btn-secondary nouveau-scan-btn" @click="reset">Nouveau scan</button>
          </div>
        </section>

        <aside class="scanner-side">
          <!-- Vente sans carte — masqué si client fidélité sélectionné -->
          <div class="card scanner-card vsc-card" v-show="!client || resultat">
            <div class="vsc-header">
              <div>
                <span class="section-kicker">Vente sans carte</span>
                <h2>Encaissement rapide</h2>
                <p class="hint">Enregistrez une vente hors programme fidélité.</p>
              </div>
              <div class="vsc-total" v-if="caSansCarteJour > 0">
                <span>CA manuel du jour</span>
                <strong>{{ formatEuro(caSansCarteJour) }}</strong>
              </div>
            </div>
            <div class="vente-row">
              <label class="label" for="montant-sans-carte">Montant (€)</label>
              <input
                id="montant-sans-carte"
                v-model.number="montantSansCarte"
                class="input montant-input compact"
                type="number"
                inputmode="decimal"
                autocomplete="off"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
              <div class="paiement-toggle">
                <button
                  class="paiement-btn"
                  :class="{ active: modePaiementVSC === 'especes' }"
                  :disabled="loadingVSC || encaissementBloque"
                  @click="validerVenteSansCarte('especes')"
                >
                  <span class="cash-icon"></span>
                  {{ loadingVSC && modePaiementVSC === 'especes' ? 'Ajout...' : 'Espèces' }}
                </button>
                <button
                  class="paiement-btn"
                  :class="{ active: modePaiementVSC === 'cb' }"
                  :disabled="loadingVSC || encaissementBloque"
                  @click="validerVenteSansCarte('cb')"
                >
                  <span class="card-icon"></span>
                  {{ loadingVSC && modePaiementVSC === 'cb' ? 'Ajout...' : 'CB' }}
                </button>
              </div>
            </div>
            <p v-if="erreurVSC" class="error">{{ erreurVSC }}</p>
            <p v-if="venteSansCarteOk" class="success-msg">{{ venteSansCarteMsg || (formatEuro(dernierMontantVSC) + " ajoutés (" + (dernierModePaiementVSC === 'cb' ? 'CB' : 'Espèces') + ") ✓") }}</p>

            <!-- Stats paiement du jour -->
            <div class="paiement-stats" v-if="caEspecesJour > 0 || caCbJour > 0">
              <div class="paiement-stat">
                <span>Espèces</span>
                <strong>{{ formatEuro(caEspecesJour) }}</strong>
              </div>
              <div class="paiement-stat">
                <span>CB</span>
                <strong>{{ formatEuro(caCbJour) }}</strong>
              </div>
            </div>
          </div>
        </aside>
      </div>

    </div>

    <div v-if="lastQuickSale" class="last-ticket-panel" role="status" aria-live="polite">
      <div class="last-ticket-main">
        <div class="last-ticket-title">
          <span class="section-kicker">Dernier ticket</span>
          <strong>{{ formatEuro(lastQuickSale.montant) }}</strong>
        </div>
        <div class="last-ticket-meta">
          <span>{{ libelleReglement(lastQuickSale.reglement) }}</span>
          <span>{{ lastQuickSale.heure }}</span>
        </div>
        <p v-if="lastQuickSaleMessage" class="last-ticket-feedback success">{{ lastQuickSaleMessage }}</p>
        <p v-if="lastQuickSaleError" class="last-ticket-feedback error-text">{{ lastQuickSaleError }}</p>
      </div>
      <div class="last-ticket-actions">
        <button
          class="last-ticket-btn"
          :class="{ active: lastQuickSale.reglement === 'especes' }"
          :disabled="lastQuickSaleUpdating || lastQuickSale.reglement === 'especes'"
          @click="corrigerReglementDernierTicket('especes')"
        >
          <span class="cash-icon"></span>
          Espèces
        </button>
        <button
          class="last-ticket-btn"
          :class="{ active: lastQuickSale.reglement === 'cb' }"
          :disabled="lastQuickSaleUpdating || lastQuickSale.reglement === 'cb'"
          @click="corrigerReglementDernierTicket('cb')"
        >
          <span class="card-icon"></span>
          CB
        </button>
      </div>
    </div>

    <!-- Popup réduction -->
    <div class="modal-overlay" v-if="popupReduction" @click.self="refuserReduction">
      <div class="modal">
        <div class="modal-icon">🎁</div>
        <h3>Réduction disponible !</h3>
        <p>
          <strong>{{ client ? client.prenom : '' }}</strong> a
          <strong>{{ client ? client.points_solde : '' }} points</strong>
          et peut bénéficier d'une réduction de
          <strong class="reduction-montant">{{ formatEuro(valeurReduction) }}</strong>
          sur cet achat.
        </p>
        <div class="modal-recap">
          <div class="recap-ligne"><span>Montant achat</span><strong>{{ formatEuro(montant) }}</strong></div>
          <div class="recap-ligne reduction"><span>Réduction</span><strong>-{{ formatEuro(valeurReduction) }}</strong></div>
          <div class="recap-ligne total"><span>Total à payer</span><strong>{{ formatEuro(montant - valeurReduction) }}</strong></div>
        </div>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="refuserReduction">Ne pas utiliser</button>
          <button class="btn btn-primary" @click="accepterReduction">Appliquer la réduction</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import api    from '../api'
import { Html5Qrcode } from 'html5-qrcode'

const ACTIVE_DAY_KEY = 'journee_active_confirmed'
const VILLE_KEY = 'marche_ville_active'
const BLOCKED_SALE_MESSAGE = 'Encaissement bloqué : ouvrez la journée du marché avant de vendre.'

export function extractCarteQrPayload(qrText) {
  const texte = String(qrText || '').trim()
  if (!texte) return null

  const parseCarteValue = (value) => {
    const raw = String(value || '').trim()
    if (/^[a-f0-9]{32}$/i.test(raw)) return { type: 'public_token', value: raw }
    if (/^\d{1,20}$/.test(raw)) return { type: 'numero_carte', value: raw }
    return null
  }

  const parsePath = (value) => {
    const match = value.match(/\/carte\/([^/?#]+)/)
    if (!match) return null
    const payload = decodeURIComponent(match[1]).trim()
    if (!payload) return null
    return parseCarteValue(payload) || { type: 'public_token', value: payload }
  }

  try {
    const url = new URL(texte)
    const fromToken = parseCarteValue(url.searchParams.get('token'))
    if (fromToken) return fromToken

    const fromUrl = parsePath(url.pathname)
    if (fromUrl) return fromUrl
  } catch {}

  const queryToken = texte.match(/[?&]token=([^&#]+)/)
  if (queryToken) {
    const fromQuery = parseCarteValue(decodeURIComponent(queryToken[1]))
    if (fromQuery) return fromQuery
  }

  const fromPath = parsePath(texte)
  if (fromPath) return fromPath

  const fromRaw = parseCarteValue(texte)
  if (fromRaw) return fromRaw

  return null
}

export default {
  name: 'ScannerView',
  components: { NavBar },
  data() {
    return {
      villeActive:           localStorage.getItem(VILLE_KEY) || '',
      // Vente sans carte
      montantSansCarte:      null,
      modePaiementVSC:       'especes',
      loadingVSC:            false,
      erreurVSC:             '',
      bonsClient:            [],
      venteSansCarteMsg:     '',
      venteSansCarteOk:      false,
      dernierMontantVSC:     0,
      dernierModePaiementVSC:'especes',
      lastQuickSale:         null,
      lastQuickSaleTimer:    null,
      lastQuickSaleUpdating: false,
      lastQuickSaleMessage:  '',
      lastQuickSaleError:    '',
      caSansCarteJour:       0,
      caEspecesJour:         0,
      caCbJour:              0,
      journeeActive:         null,
      journeeActiveLoading:  false,
      marchesOuverts:        [],
      selectionMarcheLoading: false,
      encaissementErreur:    '',
      alerteVilleLocale:     '',
      // Scanner
      recherche:      '',
      resultats:      [],
      client:         null,
      clientSelectionne: null,
      clientTrouve:   null,
      montant:        null,
      modePaiement:   'especes',
      utiliserPoints: false,
      resultat:       null,
      erreur:         '',
      messageSucces:  '',
      loading:        false,
      loadingTx:      false,
      timer:          null,
      popupReduction: false,
      cameraActive:   false,
      scanMessage:    '',
      carteAActiver: null,
      activationMode: '',
      activationForm: { prenom: '', nom: '', telephone: '' },
      activationLoading: false,
      html5QrCode:    null,
      pointsParEuro:   1,
      seuilPoints:     100,
      valeurReduction: 5,
      montantMinimum:  15,
      montantFocus:    false,
      clavierOffset:   0,
      isOnline:        typeof navigator !== 'undefined' ? navigator.onLine : true
    }
  },
  computed: {
    pointsPreview() {
      return this.montant ? Math.floor(this.montant * this.pointsParEuro) : 0
    },
    venteValidable() {
      return this.montant > 0 && !this.loadingTx && !this.encaissementBloque
    },
    encaissementBloque() {
      return this.journeeActiveLoading || !this.journeeActive
    },
    journeeActiveLabel() {
      if (this.journeeActiveLoading) return 'Vérification...'
      if (!this.journeeActive) return 'Aucune journée ouverte'
      return `${this.formatDate(this.journeeActive.date)} - ${this.journeeActive.statut === 'cloture' ? 'Clôturée' : 'Ouverte'}`
    },
    marcheActifLabel() {
      return this.journeeActive?.marche_lieu?.nom_affichage || this.villeActive || ''
    },
    reductionEligible() {
      return this.client &&
             this.client.points_solde >= this.seuilPoints &&
             this.montant >= this.montantMinimum
    },
    bonBientotExpire() {
      if (!this.bonsClient || !this.bonsClient.length) return null
      const mapped = this.bonsClient
        .filter(b => (b.statut || '').trim() !== 'perime' && (b.statut || '').trim() !== 'utilise')
        .map(b => {
          const exp = new Date(b.date_expiration)
          const now = new Date()
          now.setHours(0,0,0,0)
          exp.setHours(0,0,0,0)
          const jours = Math.ceil((exp - now) / (1000 * 60 * 60 * 24))
          return { ...b, jours_restants: jours }
        })
      if (!mapped.length) return null
      return mapped.sort((a, b) => a.jours_restants - b.jours_restants)[0]
    }
  },
  methods: {
    formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatEuro(v) {
      return Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
    },
    formatHeure(d) {
      return new Date(d || Date.now()).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    },
    libelleReglement(mode) {
      return mode === 'cb' ? 'CB' : 'Espèces'
    },
    todayIso() {
      return new Date().toISOString().split('T')[0]
    },
    normaliserVille(ville) {
      return String(ville || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[’‘`´]/g, "'")
        .replace(/[-‐‑‒–—―]/g, ' ')
        .replace(/\s+/g, ' ')
    },
    villesEquivalentes(villeA, villeB) {
      return this.normaliserVille(villeA) === this.normaliserVille(villeB)
    },
    libelleMarche(marche) {
      return marche?.marche_lieu?.nom_affichage || marche?.nom_affichage || marche?.ville || 'Marché'
    },
    marcheDepuisVille(ville) {
      if (!ville) return null
      return this.marchesOuverts.find(m => this.villesEquivalentes(m.ville, ville)) || null
    },
    paramsJourneeActive(ville = this.villeActive, marche = null) {
      const marcheOuvert = marche || this.marcheDepuisVille(ville)
      const params = {}
      if (marcheOuvert?.marche_lieu_id) params.marche_lieu_id = marcheOuvert.marche_lieu_id
      if (ville) params.ville = ville
      return params
    },
    confirmerJourneeActive(journee) {
      if (!journee?.id) return
      localStorage.setItem(ACTIVE_DAY_KEY, JSON.stringify({
        active: true,
        date: this.todayIso(),
        journee_id: journee.id,
        marche_lieu_id: journee.marche_lieu_id || '',
        nom_affichage: journee.marche_lieu?.nom_affichage || '',
        code_postal: journee.marche_lieu?.code_postal || '',
        code_insee: journee.marche_lieu?.code_insee || '',
        ville: journee.ville || '',
        confirmedAt: new Date().toISOString()
      }))
    },
    oublierJourneeActive() {
      localStorage.removeItem(ACTIVE_DAY_KEY)
    },
    synchroniserJourneeActive(journee) {
      this.journeeActive = journee
      if (journee?.ville) {
        this.villeActive = journee.ville
        localStorage.setItem(VILLE_KEY, this.villeActive)
      }
      this.encaissementErreur = ''
      this.alerteVilleLocale = ''
      this.confirmerJourneeActive(journee)
    },
    lireJourneeConfirmeeLocale() {
      try {
        const confirmed = JSON.parse(localStorage.getItem(ACTIVE_DAY_KEY) || 'null')
        if (!confirmed?.active || confirmed.date !== this.todayIso() || !confirmed.journee_id) return null
        if (confirmed.marche_lieu_id && this.journeeActive?.marche_lieu_id && confirmed.marche_lieu_id !== this.journeeActive.marche_lieu_id) return null
        if (!confirmed.marche_lieu_id && !this.villesEquivalentes(confirmed.ville, this.villeActive)) return null
        return {
          id: confirmed.journee_id,
          date: confirmed.date,
          marche_lieu_id: confirmed.marche_lieu_id || null,
          marche_lieu: confirmed.marche_lieu_id ? {
            id: confirmed.marche_lieu_id,
            nom_affichage: confirmed.nom_affichage || confirmed.ville || null,
            ville: confirmed.ville || null,
            code_postal: confirmed.code_postal || null,
            code_insee: confirmed.code_insee || null
          } : null,
          ville: confirmed.ville || null,
          statut: 'en_cours'
        }
      } catch {
        return null
      }
    },
    async chargerMarchesOuverts() {
      try {
        const res = await api.get('/journee/today')
        const rows = Array.isArray(res.data) ? res.data : []
        this.marchesOuverts = rows.filter(j => (j.statut || 'en_cours') !== 'cloture' && j.ville)
      } catch {
        this.marchesOuverts = []
      }
    },
    async selectionnerMarcheScanner(marche) {
      const ville = marche?.ville
      if (!ville || this.selectionMarcheLoading) return
      this.selectionMarcheLoading = true
      this.encaissementErreur = ''
      this.alerteVilleLocale = ''
      try {
        this.villeActive = ville
        localStorage.setItem(VILLE_KEY, ville)
        const res = await api.get('/journee/active', { params: this.paramsJourneeActive(ville, marche) })
        if (res.data?.active && res.data?.journee) {
          this.synchroniserJourneeActive(res.data.journee)
          await this.chargerBord()
          return
        }
        throw new Error(res.data?.error || BLOCKED_SALE_MESSAGE)
      } catch (err) {
        this.journeeActive = null
        this.oublierJourneeActive()
        this.encaissementErreur = err.response?.data?.error || err.message || BLOCKED_SALE_MESSAGE
      } finally {
        this.selectionMarcheLoading = false
      }
    },
    async chargerEtatJourneeActive() {
      this.journeeActiveLoading = true
      this.encaissementErreur = ''
      this.alerteVilleLocale = ''
      this.journeeActive = null

      try {
        const villeLocale = localStorage.getItem(VILLE_KEY) || this.villeActive
        this.villeActive = villeLocale
        const res = await api.get('/journee/active', { params: this.paramsJourneeActive(villeLocale) })
        if (res.data?.active && res.data?.journee) {
          this.synchroniserJourneeActive(res.data.journee)
          if (villeLocale && !this.villesEquivalentes(villeLocale, this.villeActive)) {
            this.alerteVilleLocale = 'Marché synchronisé avec la journée active.'
          }
        } else {
          this.encaissementErreur = res.data?.error || BLOCKED_SALE_MESSAGE
          this.oublierJourneeActive()
        }
      } catch (err) {
        const locale = !err.response ? this.lireJourneeConfirmeeLocale() : null
        if (locale) {
          this.journeeActive = locale
          return
        }
        this.encaissementErreur = err.response?.data?.error || BLOCKED_SALE_MESSAGE
        if (err.response && this.villeActive) {
          const villeLocale = this.villeActive
          await this.chargerMarchesOuverts()
          const marcheLocale = this.marcheDepuisVille(villeLocale)
          if (marcheLocale) {
            await this.selectionnerMarcheScanner(marcheLocale)
            return
          }
          this.alerteVilleLocale = `Le marché "${villeLocale}" enregistré sur cet appareil n’a pas de journée ouverte aujourd’hui.`
        } else if (err.response) {
          await this.chargerMarchesOuverts()
        }
        if (err.response) this.oublierJourneeActive()
      } finally {
        this.journeeActiveLoading = false
      }
    },

    // === VENTE SANS CARTE ===
    async validerVenteSansCarte(mode) {
      if (this.loadingVSC) return
      this.erreurVSC = ''
      const montant = Number(this.montantSansCarte)
      if (!montant || montant <= 0) {
        this.erreurVSC = 'Saisissez un montant positif avant de choisir le règlement.'
        this.venteSansCarteOk = false
        return
      }
      if (this.encaissementBloque) {
        this.erreurVSC = this.encaissementErreur || BLOCKED_SALE_MESSAGE
        this.venteSansCarteOk = false
        return
      }
      this.modePaiementVSC = mode
      await this.ajouterVenteSansCarte()
    },
    async ajouterVenteSansCarte() {
      if (this.loadingVSC) return
      this.loadingVSC = true
      this.erreurVSC = ''
      try {
        const res = await api.post('/journee/vente-sans-fidelite', {
          montant:       this.montantSansCarte,
          mode_paiement: this.modePaiementVSC,
          marche_lieu_id: this.journeeActive?.marche_lieu_id || null,
          ville:         this.villeActive || null
        })
        this.dernierMontantVSC      = this.montantSansCarte
        this.dernierModePaiementVSC = this.modePaiementVSC
        this.montantSansCarte       = null
        this.venteSansCarteOk       = true
        if (res.data?.offline) {
          this.venteSansCarteMsg = '⏳ Hors ligne — vente enregistree localement'
        } else {
          this.venteSansCarteMsg = ''
          if (res.data?.bons_actifs) this.bonsClient = res.data.bons_actifs
          this.caSansCarteJour   = parseFloat(res.data?.ca_sans_fidelite || 0)
          this.caEspecesJour     = parseFloat(res.data?.ca_especes || 0)
          this.caCbJour          = parseFloat(res.data?.ca_cb || 0)
        }
        this.afficherDernierTicket(res.data?.ticket_sans_carte, this.dernierMontantVSC, this.dernierModePaiementVSC)
        setTimeout(() => this.venteSansCarteOk = false, 3000)
      } catch (err) {
        console.error(err)
        this.erreurVSC = err.response?.data?.error || "Impossible d'enregistrer la vente sans carte."
        this.venteSansCarteOk = false
      } finally {
        this.loadingVSC = false
      }
    },
    afficherDernierTicket(ticket, montant, reglement) {
      clearTimeout(this.lastQuickSaleTimer)
      this.lastQuickSaleMessage = ''
      this.lastQuickSaleError = ''
      this.lastQuickSale = {
        id: ticket?.id || null,
        montant: parseFloat(ticket?.montant ?? montant ?? 0),
        reglement: ticket?.mode_paiement || reglement || 'especes',
        heure: this.formatHeure(ticket?.created_at || new Date())
      }
      this.lastQuickSaleTimer = setTimeout(() => {
        this.lastQuickSale = null
        this.lastQuickSaleMessage = ''
        this.lastQuickSaleError = ''
      }, 45000)
    },
    async corrigerReglementDernierTicket(reglement) {
      if (!this.lastQuickSale || this.lastQuickSaleUpdating || reglement === this.lastQuickSale.reglement) return
      this.lastQuickSaleMessage = ''
      this.lastQuickSaleError = ''
      if (!this.lastQuickSale.id) {
        this.lastQuickSaleError = 'Impossible de corriger ce ticket : identifiant manquant.'
        return
      }
      this.lastQuickSaleUpdating = true
      try {
        const res = await api.patch(`/clients/historique/sans_carte/${this.lastQuickSale.id}/reglement`, { reglement })
        const ticket = res.data?.ticket || {}
        this.lastQuickSale = {
          ...this.lastQuickSale,
          reglement: ticket.mode_paiement || reglement,
          montant: parseFloat(ticket.montant ?? this.lastQuickSale.montant),
          heure: this.formatHeure(ticket.created_at || new Date())
        }
        this.dernierModePaiementVSC = this.lastQuickSale.reglement
        this.caSansCarteJour = parseFloat(res.data?.ca_sans_fidelite ?? this.caSansCarteJour)
        this.caEspecesJour = parseFloat(res.data?.ca_especes ?? this.caEspecesJour)
        this.caCbJour = parseFloat(res.data?.ca_cb ?? this.caCbJour)
        this.lastQuickSaleMessage = 'Règlement corrigé.'
      } catch (err) {
        this.lastQuickSaleError = err.response?.data?.error || 'Impossible de corriger le règlement de ce ticket.'
      } finally {
        this.lastQuickSaleUpdating = false
      }
    },

    async chargerBord() {
      if (!this.villeActive) return
      try {
        const res = await api.get('/journee/today/' + encodeURIComponent(this.villeActive))
        this.caSansCarteJour = parseFloat(res.data?.ca_sans_fidelite || 0)
        this.caEspecesJour   = parseFloat(res.data?.ca_especes || 0)
        this.caCbJour        = parseFloat(res.data?.ca_cb || 0)
      } catch {}
    },

    // === INSCRIPTION ===
    inscrireDepuisRecherche() {
      const texte = this.recherche.trim()
      if (/^\d+$/.test(texte)) {
        this.$router.push({ path: '/nouveau-client', query: { numero_carte: texte } })
      } else {
        const mots   = texte.split(' ')
        const prenom = mots[0] || ''
        const nom    = mots.slice(1).join(' ') || ''
        this.$router.push({ path: '/nouveau-client', query: { prenom, nom } })
      }
    },

    // === CAMÉRA ===
    async toggleCamera() {
      if (this.cameraActive) { this.fermerCamera() } else { this.ouvrirCamera() }
    },
    async ouvrirCamera() {
      this.cameraActive = true
      await this.$nextTick()
      this.html5QrCode = new Html5Qrcode('qr-reader')
      try {
        await this.html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 150 } },
          (decodedText) => { this.onScanSuccess(decodedText) },
          () => {}
        )
      } catch (err) {
        console.error('Erreur caméra:', err)
        this.cameraActive = false
        this.erreur = "Impossible d'accéder à la caméra."
      }
    },
    async fermerCamera() {
      if (this.html5QrCode) {
        try { await this.html5QrCode.stop() } catch {}
        this.html5QrCode = null
      }
      this.cameraActive = false
    },
    async onScanSuccess(texte) {
      console.log('[SCAN DEBUG] fonction scan appelée')
      console.log('[SCAN DEBUG] valeur brute scannée =', texte)
      const payload = extractCarteQrPayload(texte)
      await this.fermerCamera()
      if (!payload) {
        this.erreur = 'QR code de carte invalide.'
        return
      }
      console.log('[SCAN DEBUG] test carte digitale', {
        valeur: payload.value,
        estDigitale: payload.type === 'public_token'
      })
      if (payload.type === 'public_token') {
        this.scanMessage = 'Carte fidélité digitale scannée'
      }
      if (payload.type === 'public_token') {
        await this.chargerClientParToken(payload.value)
      } else {
        await this.chargerClientParCarte(payload.value)
      }
    },

    // === RECHERCHE ===
    rechercherClient() {
      clearTimeout(this.timer)
      if (this.carteAActiver) {
        this.carteAActiver = null
        this.activationMode = ''
      }
      this.client = null
      this.resultats = []
      if (this.recherche.length < 2) return
      this.timer = setTimeout(async () => {
        this.loading = true
        try {
          const res      = await api.get(`/clients/recherche?q=${encodeURIComponent(this.recherche)}`)
          this.resultats = res.data
        } catch { this.resultats = [] } finally { this.loading = false }
      }, 300)
    },
    async selectionnerClient(c) {
      this.client       = c
      this.montantFocus = false
      this.carteAActiver = null
      this.activationMode = ''
      this.resultats    = []
      this.recherche    = ''
      this.montant      = null
      this.modePaiement = 'especes'
      this.utiliserPoints = false
      this.resultat     = null
      this.erreur       = ''
      this.popupReduction = false
      try {
        const res = await api.get('/transactions/bons/' + c.id)
        this.bonsClient = [...(res.data || [])]
      } catch { this.bonsClient = [] }
    },
    async selectionnerResultat(c) {
      if (this.carteAActiver && this.activationMode === 'existant') {
        await this.activerCarteRattachement(c.id)
        return
      }
      await this.selectionnerClient(c)
      await this.focusMontant()
    },
    async focusMontant() {
      await this.$nextTick()
      const input = this.$refs.montantInput
      if (!input) return
      input.scrollIntoView({ behavior: 'smooth', block: 'center' })
      input.focus({ preventScroll: true })
    },
    async chargerClientParCarte(numeroCarte) {
      try {
        const res = await api.get(`/clients/${encodeURIComponent(numeroCarte)}`)
        const client = this.extraireClientDepuisReponse(res.data)
        if (client) {
          await this.selectionnerClientDepuisScan(client, `Carte ${numeroCarte} scannée !`)
        }
      } catch (err) {
        if (err.response?.status === 404) {
          this.recherche = numeroCarte
          this.erreur    = `Carte ${numeroCarte} introuvable — inscrivez le client`
        } else {
          this.erreur = err.response?.data?.error || 'Impossible de lire cette carte.'
        }
      }
    },
    async chargerClientParToken(publicToken) {
      if (!publicToken) return
      this.erreur = ''
      this.loading = true
      try {
        const res = await api.get('/clients/scan', { params: { token: publicToken } })
        if (res.data?.status === 'not_activated') {
          console.log('[SCAN NOT ACTIVATED]', {
            digital_token: res.data.digital_token || publicToken,
            numero_carte: res.data.numero_carte
          })
          if (!res.data?.numero_carte) {
            console.warn('[CARTE] Aucun numero_carte retourné pour ce digital_token')
          }
          this.$router.push({
            path: '/nouveau-client',
            query: {
              digital_token: res.data.digital_token || publicToken,
              numero_carte: res.data.numero_carte || '',
              type: 'digital',
              activation: '1',
              message: 'Carte digitale non activée. Créez le client pour l’activer.'
            }
          })
          return
        }
        if (res.data && res.data.type === 'carte_disponible') {
          const carte = res.data.carte
          const codeCartePhysique = carte?.numero_carte_physique || carte?.public_token || publicToken
          console.log('[SCAN DIGITAL]', {
            codeCarte: codeCartePhysique,
            response: res.data,
            action: 'redirect_nouveau_client'
          })
          this.carteAActiver = carte
          this.client = null
          this.clientSelectionne = null
          this.clientTrouve = null
          this.activationMode = ''
          this.activationForm = { prenom: '', nom: '', telephone: '' }
          this.resultat = null
          this.resultats = []
          this.recherche = ''
          this.messageSucces = ''
          this.erreur = ''
          this.scanMessage = 'Carte non activée. Créez le client pour l’activer.'
          setTimeout(() => {
            if (this.scanMessage === 'Carte non activée. Créez le client pour l’activer.') {
              this.redirigerCreationCartePhysique(carte, codeCartePhysique)
            }
          }, 700)
          return
        }
        const client = this.extraireClientDepuisReponse(res.data)
        if (!client?.id) {
          this.erreur = 'Carte introuvable'
          return
        }
        const nomClient = [client.prenom, client.nom].filter(Boolean).join(' ')
        await this.selectionnerClientDepuisScan(client, `Client ${nomClient} sélectionné`)
      } catch (err) {
        if (err.response?.status === 401) {
          this.erreur = 'Session expirée, reconnectez-vous'
        } else if (err.response?.status === 410 || err.response?.status === 403) {
          this.erreur = err.response?.data?.error || 'Carte désactivée'
        } else if (err.response?.status === 404) {
          console.warn('[CARTE] Aucun numero_carte retourné pour ce digital_token')
          this.scanMessage = 'Carte digitale non activée. Créez le client pour l’activer.'
          this.$router.push({
            path: '/nouveau-client',
            query: {
              carte: publicToken,
              digital_token: publicToken,
              type: 'digital',
              activation: '1',
              message: 'Carte digitale non activée. Créez le client pour l’activer.'
            }
          })
          return
        } else {
          this.erreur = 'Impossible de charger la carte'
        }
      } finally {
        this.loading = false
      }
    },
    creerClientAvecCarte() {
      if (!this.carteAActiver) return
      this.redirigerCreationCartePhysique(this.carteAActiver)
    },
    redirigerCreationCartePhysique(carte, codeScanne = '') {
      const codePhysique = codeScanne || carte?.numero_carte_physique || carte?.public_token || ''
      if (!carte?.numero_carte || !codePhysique) {
        this.erreur = 'Carte non activée, mais informations de carte incomplètes.'
        return
      }
      this.$router.push({
        path: '/nouveau-client',
        query: {
          numero_carte_physique: codePhysique,
          numero_carte: carte.numero_carte,
          type: 'physique',
          activation: '1',
          message: 'Carte non activée. Créez le client pour l’activer.'
        }
      })
    },
    rattacherClientExistant() {
      this.scanMessage = 'Sélectionnez un client existant pour rattacher cette carte'
      setTimeout(() => {
        if (this.scanMessage === 'Sélectionnez un client existant pour rattacher cette carte') {
          this.scanMessage = ''
        }
      }, 3000)
    },
    async activerCarteCreation() {
      if (!this.carteAActiver || this.activationLoading) return
      this.activationLoading = true
      this.erreur = ''
      try {
        const res = await api.post('/clients/activer-carte', {
          public_token: this.carteAActiver.public_token,
          prenom: this.activationForm.prenom,
          nom: this.activationForm.nom,
          telephone: this.activationForm.telephone
        })
        await this.selectionnerClientDepuisScan(res.data.client, `Carte ${this.carteAActiver.numero_carte} activée`)
        this.carteAActiver = null
        this.activationMode = ''
        this.activationForm = { prenom: '', nom: '', telephone: '' }
      } catch (err) {
        this.erreur = err.response?.data?.error || "Impossible d'activer cette carte."
      } finally {
        this.activationLoading = false
      }
    },
    async activerCarteRattachement(clientId) {
      if (!this.carteAActiver || this.activationLoading) return
      this.activationLoading = true
      this.erreur = ''
      try {
        const res = await api.post('/clients/activer-carte', {
          public_token: this.carteAActiver.public_token,
          client_id: clientId
        })
        await this.selectionnerClientDepuisScan(res.data.client, `Carte ${this.carteAActiver.numero_carte} rattachée`)
        this.carteAActiver = null
        this.activationMode = ''
      } catch (err) {
        this.erreur = err.response?.data?.error || "Impossible de rattacher cette carte."
      } finally {
        this.activationLoading = false
      }
    },
    extraireClientDepuisReponse(data) {
      const client = data?.client || data?.data || data
      return client?.id ? client : null
    },
    async selectionnerClientDepuisScan(client, message) {
      await this.selectionnerClient(client)
      await this.focusMontant()
      if (this.client?.id === client.id && !this.resultat) {
        this.scanMessage = message
        setTimeout(() => this.scanMessage = '', 3000)
      }
    },

    // === TRANSACTION ===
    validerSiPossible() {
      if (this.venteValidable) this.validerTransaction()
    },
    async majClavierOffset() {
      // Hauteur du clavier virtuel = partie du layout viewport cachée sous le visual viewport
      const vv = window.visualViewport
      this.clavierOffset = vv ? Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)) : 0
      if (!this.montantFocus) return
      await this.$nextTick()
      // Le bouton fixé ne doit pas recouvrir le champ montant
      const input = this.$refs.montantInput
      const btn   = this.$refs.btnValider
      if (!input || !btn) return
      const chevauchement = input.getBoundingClientRect().bottom - (btn.getBoundingClientRect().top - 12)
      if (chevauchement > 0) window.scrollBy(0, chevauchement)
    },
    async validerTransaction() {
      this.erreur = ''
      if (this.encaissementBloque) {
        this.erreur = this.encaissementErreur || BLOCKED_SALE_MESSAGE
        return
      }
      if (this.reductionEligible && !this.utiliserPoints) {
        this.popupReduction = true
        return
      }
      await this.enregistrerTransaction()
    },
    accepterReduction() {
      this.popupReduction = false
      this.utiliserPoints = true
      this.enregistrerTransaction()
    },
    refuserReduction() {
      this.popupReduction = false
      this.utiliserPoints = false
      this.enregistrerTransaction()
    },
    async enregistrerTransaction() {
      this.loadingTx = true
      try {
        const res = await api.post('/transactions', {
          client_id:       this.client.id,
          montant_achat:   this.montant,
          utiliser_points: this.utiliserPoints,
          mode_paiement:   this.modePaiement,
          marche_lieu_id:  this.journeeActive?.marche_lieu_id || null,
          ville:           this.villeActive || null
        })
        this.resultat = res.data
        this.client   = null
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Erreur lors de la transaction'
      } finally {
        this.loadingTx      = false
        this.utiliserPoints = false
      }
    },
    reset() {
      this.recherche      = ''
      this.resultats      = []
      this.client         = null
      this.carteAActiver  = null
      this.activationMode = ''
      this.activationForm = { prenom: '', nom: '', telephone: '' }
      this.montant        = null
      this.modePaiement   = 'especes'
      this.utiliserPoints = false
      this.resultat       = null
      this.erreur         = ''
      this.popupReduction = false
      this.scanMessage    = ''
      this.fermerCamera()
    },
    async chargerRegles() {
      try {
        const res = await api.get('/auth/regles')
        if (res.data) {
          this.pointsParEuro   = res.data.points_par_euro
          this.seuilPoints     = res.data.seuil_points
          this.valeurReduction = parseFloat(res.data.valeur_reduction)
        }
      } catch {}
    },
    updateOnlineStatus() {
      this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true
      if (this.isOnline) this.chargerEtatJourneeActive()
    }
  },
  async mounted() {
    await Promise.all([
      this.chargerEtatJourneeActive(),
      this.chargerRegles()
    ])
    await this.chargerBord()
    const searchParams = new URLSearchParams(window.location.search)
    const token = this.$route?.query?.token || searchParams.get('token')
    if (token) {
      await this.chargerClientParToken(token)
    }
    const carte = this.$route?.query?.carte || this.$route?.query?.numero_carte || searchParams.get('carte') || searchParams.get('numero_carte')
    if (carte) { await this.chargerClientParCarte(carte) }
    window.addEventListener('online', this.updateOnlineStatus)
    window.addEventListener('offline', this.updateOnlineStatus)
    window.visualViewport?.addEventListener('resize', this.majClavierOffset)
    window.visualViewport?.addEventListener('scroll', this.majClavierOffset)
  },
  unmounted() {
    window.removeEventListener('online', this.updateOnlineStatus)
    window.removeEventListener('offline', this.updateOnlineStatus)
    window.visualViewport?.removeEventListener('resize', this.majClavierOffset)
    window.visualViewport?.removeEventListener('scroll', this.majClavierOffset)
    clearTimeout(this.lastQuickSaleTimer)
    this.fermerCamera()
  }
}
</script>

<style scoped>
.scanner-page {
  max-width: 1180px;
  padding: 24px 18px 44px;
}

.scanner-hero {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 24px;
  margin-bottom: 18px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: var(--fm-card-radius);
  background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 62%, #EEF6FF 100%);
  box-shadow: var(--fm-shadow-soft);
}

.hero-copy {
  min-width: 0;
}

.hero-kicker,
.section-kicker {
  display: inline-flex;
  color: var(--fm-accent);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.scanner-hero h1 {
  color: var(--fm-navy);
  font-size: 32px;
  font-weight: 800;
  margin-top: 4px;
}

.scanner-hero p {
  max-width: 620px;
  margin-top: 8px;
  color: var(--fm-muted);
  font-size: 14px;
}

.hero-actions {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.status-pill,
.market-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 8px 13px;
  border-radius: 999px;
  border: 1px solid var(--fm-border);
  background: var(--fm-surface);
  color: var(--fm-text);
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.status-pill.online {
  border-color: #BBF7D0;
  background: var(--fm-success-soft);
  color: #166534;
}

.status-pill.offline,
.market-pill.warn {
  border-color: #FDE68A;
  background: var(--fm-warning-soft);
  color: #92400E;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.14);
}

.offline .status-dot {
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.14);
}

.pill-icon,
.btn-plus,
.camera-icon,
.cash-icon,
.card-icon,
.success-icon,
.empty-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.location-icon {
  width: 15px;
  height: 15px;
  border: 2px solid currentColor;
  border-radius: 999px 999px 999px 2px;
  transform: rotate(-45deg);
}

.location-icon::after {
  content: '';
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: currentColor;
}

.alert-icon {
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-radius: 999px;
}

.alert-icon::before,
.alert-icon::after {
  content: '';
  position: absolute;
  width: 2px;
  background: currentColor;
  border-radius: 999px;
}

.alert-icon::before {
  height: 6px;
  top: 3px;
}

.alert-icon::after {
  height: 2px;
  bottom: 3px;
}

.nouveau-btn {
  width: auto;
  min-height: 42px;
  padding: 10px 16px;
  white-space: nowrap;
}

.btn-plus {
  width: 16px;
  height: 16px;
}

.btn-plus::before,
.btn-plus::after {
  content: '';
  position: absolute;
  background: currentColor;
  border-radius: 999px;
}

.btn-plus::before {
  left: 2px;
  right: 2px;
  top: 7px;
  height: 2px;
}

.btn-plus::after {
  top: 2px;
  bottom: 2px;
  left: 7px;
  width: 2px;
}

.scanner-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 380px);
  gap: 18px;
  align-items: start;
}

.active-day-panel {
  margin-bottom: 18px;
  padding: 16px;
  border: 1px solid #BBF7D0;
  border-radius: var(--fm-card-radius);
  background: var(--fm-success-soft);
}

.active-day-panel.blocked {
  border-color: #FECACA;
  background: var(--fm-danger-soft);
}

.active-day-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  gap: 14px;
  align-items: center;
}

.active-day-grid strong {
  display: block;
  margin-top: 3px;
  color: var(--fm-navy);
  font-size: 17px;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.active-day-action {
  width: auto;
  white-space: nowrap;
}

.active-day-alert {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #FECACA;
  border-radius: 14px;
  background: #FFFFFF;
  color: #B91C1C;
  font-size: 13px;
  font-weight: 800;
}

.market-choice-list {
  margin-top: 14px;
}

.market-choice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-top: 8px;
}

.market-choice-btn {
  display: flex;
  min-width: 0;
  min-height: 62px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 3px;
  padding: 11px 13px;
  border: 1px solid #BFDBFE;
  border-radius: 14px;
  background: #FFFFFF;
  color: var(--fm-navy);
  text-align: left;
  cursor: pointer;
}

.market-choice-btn:disabled {
  cursor: wait;
  opacity: 0.7;
}

.market-choice-btn strong {
  width: 100%;
  font-size: 14px;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.market-choice-btn span {
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 800;
}

.scanner-main,
.scanner-side {
  min-width: 0;
}

.scanner-card {
  padding: 22px;
  margin-bottom: 18px;
  border-radius: var(--fm-card-radius);
  box-shadow: var(--fm-shadow-subtle);
}

.section-heading,
.vsc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}

.section-heading h2,
.vsc-header h2,
.client-info h2,
.resultat-card h2 {
  color: var(--fm-navy);
  font-size: 20px;
  font-weight: 800;
  margin-top: 3px;
}

.hint {
  color: var(--fm-muted);
  font-size: 13px;
  margin-top: 8px;
}

.search-row {
  display: flex;
  gap: 10px;
}

.search-input {
  min-height: 54px;
  font-weight: 600;
}

.btn-camera {
  width: 54px;
  height: 54px;
  border: 1px solid var(--fm-border);
  border-radius: 16px;
  background: var(--fm-surface);
  color: var(--fm-accent);
  cursor: pointer;
  transition: transform var(--fm-transition), border-color var(--fm-transition), background var(--fm-transition), box-shadow var(--fm-transition), color var(--fm-transition);
}

.btn-camera:hover {
  transform: translateY(-1px);
  border-color: #BFDBFE;
  background: var(--fm-accent-soft);
}

.btn-camera.active {
  border-color: var(--fm-accent);
  background: var(--fm-accent);
  color: white;
  box-shadow: 0 12px 24px rgba(30, 111, 220, 0.2);
}

.camera-icon {
  width: 24px;
  height: 18px;
  border: 2px solid currentColor;
  border-radius: 6px;
}

.camera-icon::before {
  content: '';
  position: absolute;
  width: 9px;
  height: 9px;
  border: 2px solid currentColor;
  border-radius: 999px;
}

.camera-icon::after {
  content: '';
  position: absolute;
  top: -5px;
  left: 4px;
  width: 10px;
  height: 5px;
  border-radius: 4px 4px 0 0;
  background: currentColor;
}

.camera-container {
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #0F172A;
  border-radius: 18px;
  background: #0F172A;
  overflow: hidden;
}

.camera-topline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  color: white;
  font-size: 13px;
  font-weight: 800;
}

.camera-close {
  min-height: 36px;
  padding: 7px 12px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

#qr-reader {
  width: 100%;
  border-radius: 14px;
  overflow: hidden;
  background: #000;
}

.scan-success,
.success-msg,
.loading-box {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 800;
}

.scan-success,
.success-msg {
  border: 1px solid #BBF7D0;
  background: var(--fm-success-soft);
  color: #166534;
}

.success-icon {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: var(--fm-success);
}

.success-icon::after {
  content: '';
  position: absolute;
  width: 9px;
  height: 5px;
  border-left: 2px solid white;
  border-bottom: 2px solid white;
  transform: rotate(-45deg);
}

.loading-box {
  border: 1px solid #BFDBFE;
  background: var(--fm-accent-soft);
  color: #1D4ED8;
}

.loader-dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: currentColor;
  animation: pulse 1.2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}

.resultats {
  margin-top: 16px;
  border: 1px solid var(--fm-border);
  border-radius: 18px;
  overflow: hidden;
}

.resultat-item {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 13px;
  padding: 14px;
  border-bottom: 1px solid var(--fm-border);
  cursor: pointer;
  transition: background var(--fm-transition);
}

.resultat-item:last-child {
  border-bottom: none;
}

.resultat-item:hover {
  background: #F8FAFC;
}

.resultat-avatar,
.client-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 16px;
  background: var(--fm-navy);
  color: white;
  font-weight: 800;
}

.resultat-avatar {
  width: 44px;
  height: 44px;
  font-size: 14px;
}

.resultat-info {
  min-width: 0;
}

.resultat-info strong {
  display: block;
  color: var(--fm-text);
  font-size: 15px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resultat-info span {
  display: block;
  color: var(--fm-muted);
  font-size: 12px;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resultat-points {
  min-width: 62px;
  padding: 8px 10px;
  border-radius: 14px;
  background: var(--fm-success-soft);
  text-align: center;
}

.resultat-points strong {
  display: block;
  color: #166534;
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
}

.resultat-points span {
  color: #15803D;
  font-size: 11px;
  font-weight: 800;
}

.aucun-resultat-box {
  margin-top: 16px;
  padding: 20px;
  border: 1px dashed #CBD5E1;
  border-radius: 18px;
  background: #F8FAFC;
  text-align: center;
}

.empty-icon {
  width: 42px;
  height: 42px;
  margin: 0 auto 10px;
  border-radius: 16px;
  background: var(--fm-accent-soft);
}

.empty-icon::before,
.empty-icon::after {
  content: '';
  position: absolute;
  background: var(--fm-accent);
  border-radius: 999px;
}

.empty-icon::before {
  width: 16px;
  height: 2px;
}

.empty-icon::after {
  width: 2px;
  height: 16px;
}

.aucun-resultat-box p {
  font-size: 14px;
}

.empty-action {
  width: auto;
  margin-top: 12px;
  padding-inline: 18px;
}

.activation-card {
  margin-top: 16px;
  padding: 18px;
  border: 1px solid #BFDBFE;
  border-radius: 8px;
  background: var(--fm-accent-soft);
}

.activation-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.activation-badge {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 5px 10px;
  border: 1px solid #BBF7D0;
  border-radius: 999px;
  background: var(--fm-success-soft);
  color: #166534;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.activation-card h3 {
  margin-top: 4px;
  color: var(--fm-navy);
  font-size: 24px;
  font-weight: 800;
}

.activation-number {
  margin: 6px 0 0;
  color: var(--fm-navy);
  font-size: 20px;
  font-weight: 800;
}

.activation-hint {
  margin-top: 6px;
}

.activation-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.activation-form {
  margin-top: 14px;
}

.form-grid.two {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.client-header {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid var(--fm-border);
}

.client-identity {
  display: flex;
  gap: 14px;
  min-width: 0;
}

.client-avatar {
  width: 58px;
  height: 58px;
  background: linear-gradient(180deg, var(--fm-navy) 0%, var(--fm-navy-soft) 100%);
  font-size: 18px;
}

.client-info {
  min-width: 0;
}

.client-tel,
.client-carte {
  font-size: 13px;
}

.client-carte {
  color: #94A3B8;
  margin-top: 2px;
}

.client-points {
  min-width: 160px;
  padding: 14px;
  border: 1px solid #BBF7D0;
  border-radius: 18px;
  background: var(--fm-success-soft);
  text-align: center;
}

.points-value {
  display: block;
  color: #166534;
  font-size: 36px;
  font-weight: 800;
  line-height: 1;
}

.points-label,
.points-seuil,
.points-dispo {
  display: block;
  font-size: 12px;
  font-weight: 700;
}

.points-label {
  color: #15803D;
  margin-top: 4px;
}

.points-seuil {
  color: var(--fm-muted);
  margin-top: 8px;
}

.points-dispo {
  color: #166534;
  margin-top: 8px;
}

.bon-alerte {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 700;
}

.bon-warning {
  background: var(--fm-warning-soft);
  border: 1px solid #FDE68A;
  color: #92400E;
}

.bon-urgent {
  background: var(--fm-danger-soft);
  border: 1px solid #FECACA;
  color: #B91C1C;
  animation: pulse 1.5s infinite;
}

.bon-alerte-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.sale-panel {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(220px, 0.8fr);
  gap: 16px;
  align-items: end;
}

.montant-input {
  min-height: 72px;
  padding: 14px 18px;
  color: var(--fm-navy);
  font-size: 34px;
  font-weight: 800;
  text-align: center;
}

.montant-input.compact {
  min-height: 64px;
  font-size: 30px;
}

.points-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding: 11px 13px;
  border-radius: 14px;
  background: var(--fm-success-soft);
  color: #166534;
  font-size: 14px;
  font-weight: 700;
}

.points-preview strong {
  font-size: 16px;
  font-weight: 800;
}

.paiement-toggle {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.paiement-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 58px;
  padding: 12px 14px;
  border: 1px solid var(--fm-border);
  border-radius: 16px;
  background: var(--fm-surface);
  color: var(--fm-text);
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: transform var(--fm-transition), border-color var(--fm-transition), background var(--fm-transition), color var(--fm-transition), box-shadow var(--fm-transition);
}

.paiement-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: #BFDBFE;
  background: #F8FAFC;
}

.paiement-btn:disabled {
  cursor: not-allowed;
  opacity: 0.64;
  transform: none;
}

.paiement-btn.active {
  border-color: var(--fm-accent);
  background: var(--fm-accent);
  color: white;
  box-shadow: 0 12px 24px rgba(30, 111, 220, 0.18);
}

.cash-icon {
  width: 18px;
  height: 18px;
  border: 2px solid currentColor;
  border-radius: 999px;
}

.cash-icon::after {
  content: '';
  position: absolute;
  width: 8px;
  height: 2px;
  background: currentColor;
  border-radius: 999px;
}

.card-icon {
  width: 20px;
  height: 15px;
  border: 2px solid currentColor;
  border-radius: 5px;
}

.card-icon::after {
  content: '';
  position: absolute;
  left: 3px;
  right: 3px;
  top: 4px;
  height: 2px;
  background: currentColor;
}

.error {
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid #FECACA;
  border-radius: 14px;
  background: var(--fm-danger-soft);
  color: #B91C1C;
  font-size: 13px;
  font-weight: 800;
}

.actions {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 12px;
  margin-top: 18px;
}

.vsc-card {
  transition: opacity var(--fm-transition), transform var(--fm-transition);
}

.vsc-card[style*="display: none"] {
  display: none !important;
}

.vsc-header {
  flex-direction: column;
  align-items: stretch;
}

.vsc-total {
  padding: 13px 14px;
  border: 1px solid #BBF7D0;
  border-radius: 16px;
  background: var(--fm-success-soft);
}

.vsc-total span,
.paiement-stat span {
  display: block;
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 700;
}

.vsc-total strong {
  display: block;
  color: #166534;
  font-size: 24px;
  font-weight: 800;
  margin-top: 2px;
}

.vente-row {
  display: grid;
  gap: 12px;
}

.add-vsc-btn {
  width: 100%;
}

.add-vsc-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.paiement-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--fm-border);
}

.paiement-stat {
  padding: 13px;
  border: 1px solid var(--fm-border);
  border-radius: 16px;
  background: #F8FAFC;
}

.paiement-stat strong {
  display: block;
  color: var(--fm-navy);
  font-size: 17px;
  font-weight: 800;
  margin-top: 3px;
}

.resultat-card {
  text-align: center;
}

.resultat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  height: 62px;
  margin-bottom: 14px;
  border-radius: 22px;
  background: var(--fm-success-soft);
  color: #166534;
  font-size: 34px;
  font-weight: 800;
}

.resultat-msg {
  margin: 10px auto 18px;
  max-width: 520px;
}

.resultat-detail {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  max-width: 520px;
  margin: 0 auto;
}

.stat {
  padding: 16px;
  border: 1px solid var(--fm-border);
  border-radius: 18px;
  background: #F8FAFC;
}

.stat span {
  display: block;
  color: var(--fm-muted);
  font-size: 12px;
  font-weight: 700;
}

.stat strong {
  display: block;
  color: var(--fm-navy);
  font-size: 22px;
  font-weight: 800;
  margin-top: 4px;
}

.reduction-applied {
  color: #166534 !important;
}

.nouveau-scan-btn {
  width: auto;
  margin-top: 18px;
  padding-inline: 20px;
}

.bon-genere-card {
  max-width: 420px;
  margin: 0 auto 18px;
  padding: 18px;
  border: 1px solid #BBF7D0;
  border-radius: 18px;
  background: var(--fm-success-soft);
}

.bon-genere-titre {
  color: #166534;
  font-size: 14px;
  font-weight: 800;
}

.bon-genere-valeur {
  color: #166534;
  font-size: 36px;
  font-weight: 800;
}

.bon-genere-exp {
  color: #15803D;
  font-size: 12px;
  font-weight: 700;
}

.last-ticket-panel {
  position: fixed;
  left: calc(248px + 18px);
  right: 18px;
  bottom: 22px;
  z-index: 99;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(220px, 300px);
  gap: 14px;
  align-items: center;
  max-width: 860px;
  margin: 0 auto;
  padding: 14px;
  border: 1px solid rgba(191, 219, 254, 0.9);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 22px 55px rgba(15, 23, 42, 0.18);
  backdrop-filter: blur(16px);
}

.last-ticket-main {
  min-width: 0;
}

.last-ticket-title,
.last-ticket-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.last-ticket-title strong {
  color: var(--fm-navy);
  font-size: 24px;
  font-weight: 800;
  white-space: nowrap;
}

.last-ticket-meta {
  margin-top: 5px;
  color: var(--fm-muted);
  font-size: 13px;
  font-weight: 800;
}

.last-ticket-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.last-ticket-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 52px;
  padding: 10px 12px;
  border: 1px solid var(--fm-border);
  border-radius: 14px;
  background: var(--fm-surface);
  color: var(--fm-text);
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: transform var(--fm-transition), border-color var(--fm-transition), background var(--fm-transition), color var(--fm-transition), box-shadow var(--fm-transition);
}

.last-ticket-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: #BFDBFE;
  background: #F8FAFC;
}

.last-ticket-btn.active {
  border-color: var(--fm-accent);
  background: var(--fm-accent);
  color: white;
  box-shadow: 0 12px 24px rgba(30, 111, 220, 0.18);
}

.last-ticket-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.last-ticket-feedback {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 800;
}

.last-ticket-feedback.success {
  color: #166534;
}

.last-ticket-feedback.error-text {
  color: #B91C1C;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(6px);
}

.modal {
  width: 100%;
  max-width: 460px;
  padding: 28px;
  border: 1px solid rgba(226, 232, 240, 0.85);
  border-radius: 24px;
  background: var(--fm-surface);
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
  text-align: center;
}

.modal-icon {
  font-size: 46px;
  margin-bottom: 10px;
}

.modal h3 {
  color: var(--fm-navy);
  font-size: 22px;
  font-weight: 800;
  margin-bottom: 10px;
}

.modal p {
  color: var(--fm-muted);
  font-size: 15px;
  line-height: 1.6;
}

.reduction-montant {
  color: #166534;
  font-size: 20px;
}

.modal-recap {
  margin: 18px 0;
  padding: 16px;
  border: 1px solid var(--fm-border);
  border-radius: 18px;
  background: #F8FAFC;
  text-align: left;
}

.recap-ligne {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid var(--fm-border);
  font-size: 15px;
}

.recap-ligne:last-child {
  border-bottom: none;
}

.recap-ligne span {
  color: var(--fm-muted);
}

.recap-ligne strong {
  color: var(--fm-text);
  font-weight: 800;
}

.recap-ligne.reduction strong {
  color: #B91C1C;
}

.recap-ligne.total span,
.recap-ligne.total strong {
  color: var(--fm-navy);
  font-size: 17px;
  font-weight: 800;
}

.recap-ligne.total strong {
  color: #166534;
}

.modal-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 1180px) {
  .scanner-grid {
    grid-template-columns: 1fr;
  }

  .active-day-grid {
    grid-template-columns: 1fr;
  }

  .active-day-action {
    width: 100%;
  }

  .vsc-card {
    margin-bottom: 18px;
  }

  .vente-row {
    grid-template-columns: minmax(170px, 1fr) minmax(240px, 1fr) auto;
    align-items: end;
  }

  .vente-row .label {
    grid-column: 1 / -1;
    margin-bottom: -4px;
  }

  .add-vsc-btn {
    width: auto;
  }
}

@media (max-width: 720px) {
  .scanner-page {
    padding: 14px 12px 178px;
  }

  .scanner-hero {
    padding: 20px;
    flex-direction: column;
  }

  .scanner-hero h1 {
    font-size: 29px;
  }

  .hero-actions,
  .nouveau-btn {
    width: 100%;
  }

  .status-pill,
  .market-pill {
    flex: 1 1 auto;
    justify-content: center;
  }

  .scanner-card {
    padding: 18px;
  }

  .search-card {
    margin-bottom: 14px;
  }

  .section-heading {
    align-items: center;
  }

  .section-heading h2 {
    font-size: 19px;
  }

  .search-input {
    min-height: 58px;
    font-size: 16px;
  }

  .btn-camera {
    width: 58px;
    height: 58px;
    border-radius: 18px;
  }

  .client-header,
  .sale-panel,
  .actions,
  .activation-actions,
  .form-grid.two,
  .vente-row,
  .resultat-detail,
  .modal-actions {
    grid-template-columns: 1fr;
  }

  .client-header {
    display: grid;
  }

  .client-points {
    min-width: 0;
  }

  .paiement-toggle {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .montant-input {
    min-height: 68px;
    font-size: 30px;
  }

  .paiement-btn {
    min-height: 60px;
  }

  .btn-valider {
    min-height: 64px;
    font-size: 18px;
  }

  /* Pendant la saisie du montant, Valider reste collé au-dessus du clavier */
  .btn-valider--clavier {
    position: fixed;
    left: 12px; right: 12px;
    width: auto;
    bottom: calc(var(--clavier-offset, 0px) + 8px);
    z-index: 110;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25);
  }

  .add-vsc-btn,
  .nouveau-scan-btn,
  .empty-action {
    width: 100%;
  }

  .last-ticket-panel {
    left: 12px;
    right: 12px;
    bottom: 92px;
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 13px;
    border-radius: 16px;
  }

  .last-ticket-title strong {
    font-size: 22px;
  }

  .last-ticket-btn {
    min-height: 52px;
  }
}

@media (max-width: 720px) and (max-height: 500px) {
  .montant-input {
    min-height: 52px;
    font-size: 24px;
  }

  .btn-valider--clavier {
    min-height: 48px;
  }
}

@media (max-width: 430px) {
  .resultat-item {
    grid-template-columns: 40px minmax(0, 1fr);
  }

  .resultat-points {
    grid-column: 1 / -1;
    min-width: 0;
  }

  .paiement-btn {
    min-height: 56px;
    padding-inline: 10px;
  }

  .last-ticket-title,
  .last-ticket-meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 3px;
  }

  .camera-container {
    padding: 10px;
  }
}
</style>
